import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Copy, Link2 } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';
import { useState } from 'react';

// Tracking link shape from /api/v1/tracking-links. Replaces the previous
// `any[]` typing — gives editor autocomplete and catches future field
// renames at compile time instead of silent undefined rendering.
interface TrackingLink {
  campaignName: string;
  domain: string;
  isDefaultDomain: boolean;
  url: string;
}

export function TrackingLinksPage() {
  const links = useQuery({
    queryKey: ['tracking-links'],
    queryFn: () => apiClient.get<TrackingLink[]>('/api/v1/tracking-links'),
  });
  const [copied, setCopied] = useState<string | null>(null);

  const onCopy = (url: string) => {
    copyToClipboard(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-widest">My Tracking Links</h1>
        <p className="text-sm text-text-secondary font-mono">// {links.data?.length ?? 0} ready-to-use URLs</p>
      </div>

      <div className="grid gap-3">
        {(links.data ?? []).map((l, i) => (
          <Card key={i} className="!p-4">
            <div className="flex items-center gap-4">
              <Link2 size={20} className="text-neon-cyan shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm font-bold">{l.campaignName}</div>
                <div className="text-[11px] text-text-tertiary font-mono mb-2">
                  {l.domain} {l.isDefaultDomain && '· default'}
                </div>
                <code className="block text-xs font-mono text-neon-cyan bg-bg-tertiary px-3 py-2 rounded break-all">
                  {l.url}
                </code>
              </div>
              <Button
                variant={copied === l.url ? 'magenta' : 'neon'}
                size="sm"
                icon={<Copy size={12} />}
                onClick={() => onCopy(l.url)}
              >
                {copied === l.url ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </Card>
        ))}
        {(links.data ?? []).length === 0 && !links.isLoading && (
          <Card>
            <div className="text-center py-12 text-text-tertiary">
              <Link2 className="mx-auto mb-2" size={32} />
              <p className="font-mono text-xs">No active campaigns yet. Ask admin to set up campaigns.</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
